// pages/posts/index.tsx
import { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useState, useRef } from 'react'

// Domain
import { Topic } from '@/domains/topic'
import { Meta, PostListItem } from '@/domains/post'

// Components
import { MainBlog } from '@/components/blog/main/MainBlog'
import { IntroBlog } from '@/components/blog/intro/IntroBlog'
import List from '@/components/blog/posts/List'

import { fetchBlogPageData, fetchBlogPosts, getStoryblokVersion } from '@/data/storyblockApi'

const PAGE_SIZE = 6

const BlogPage = ({
  initialPosts,
  initialTopics,
  initialMeta,
  currentTag,
}: {
  initialPosts: PostListItem[]
  initialTopics: Topic[]
  initialMeta: Meta
  currentTag: string | null
}) => {
  const previousTag = useRef<string | null>(currentTag)
  const [postsList, setPostList] = useState<PostListItem[]>(initialPosts)
  const [pagination, setPagination] = useState<Meta['pagination']>(initialMeta?.pagination || null)
  const [topics, setTopics] = useState<Topic[]>(initialTopics)
  const [hasFetchedInitialData, setHasFetchedInitialData] = useState(false)
  const router = useRouter()
  const { locale, query } = router
  const currentLocale = locale || 'en'

  useEffect(() => {
    const updatePostList = async () => {
      if (!hasFetchedInitialData) {
        setHasFetchedInitialData(true)
        return // Skip the initial fetch when component first renders
      }

      try {
        const tag = (query.topic as string) || ''
        const searchQuery = (query.search as string) || ''
        // Check if tag has changed - if so, reset to page 1
        let page = Number(query.page) || 1
        if (tag !== previousTag.current) {
          page = 1
          // Update URL to reflect page 1 when changing tags
          if (query.page && query.page !== '1') {
            router.push(
              {
                pathname: router.pathname,
                query: { ...router.query, tag, page: 1 },
              },
              undefined,
              { shallow: true }
            )
            return // Exit early as router change will trigger this effect again
          }
        }

        previousTag.current = tag

        // Use utility function instead of direct API call
        const { posts, pagination } = await fetchBlogPosts({
          page,
          perPage: 12, // Or your PAGE_SIZE constant
          tag,
          searchQuery,
          locale: currentLocale,
          version: 'published', // Or getStoryblokVersion() if you want to use dynamic versioning
          //   version: getStoryblokVersion(),
        })

        setPostList(posts)
        setPagination(pagination)
      } catch (error) {
        console.error('Error fetching posts from Storyblok:', error)
        setPostList([])
        setPagination({
          page: 1,
          pageSize: PAGE_SIZE,
          pageCount: 0,
          total: 0,
        })
      }
    }

    updatePostList()
  }, [router.query, currentLocale, hasFetchedInitialData])

  return (
    <MainBlog>
      <IntroBlog topics={topics} />
      <List posts={postsList} pagination={pagination} />
    </MainBlog>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  // Extract parameters from context
  const currentLocale = 'en'

  try {
    // Fetch topics (tags) from Storyblok
    // Use utility function to fetch initial data
    const { posts, topics, pagination } = await fetchBlogPageData({
      page: 1,
      perPage: 12,
      locale: currentLocale,
      version: getStoryblokVersion(),
    })

    return {
      props: {
        initialPosts: posts,
        initialTopics: topics,
        initialMeta: {
          pagination: pagination,
        },
      },
      revalidate: 600, // ISR: Regenerate every 10 minutes
    }
  } catch (error) {
    console.error('Error fetching data from Storyblok:', error)
    return {
      props: {
        initialPosts: [],
        initialTopics: [],
        initialMeta: {
          pagination: {
            page: 1,
            pageSize: PAGE_SIZE,
            pageCount: 0,
            total: 0,
          },
        },
        currentPage: 1,
        currentTag: null,
      },
      revalidate: 60, // Try again sooner if there was an error
    }
  }
}

export default BlogPage
